import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

import { AppLayoutContext } from '../../providers/AppLayout'
import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Divider,
  Button,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material'
import RateReviewIcon from '@mui/icons-material/RateReview'
import AssignmentIcon from '@mui/icons-material/Assignment'
import HospitalIcon from '../../icons/HospitalIcon'
import AgencyIcon from '../../icons/AgencyIcon'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PeopleIcon from '@mui/icons-material/People'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DashboardIcon from '@mui/icons-material/Dashboard'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import InsightsIcon from '@mui/icons-material/Insights'
import StorageIcon from '@mui/icons-material/Storage'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import SpeedIcon from '@mui/icons-material/Speed'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import DateRangePicker, {
  DateRange,
  getPresetDateRange,
} from '../../components/DateRangePicker/DateRangePicker.component'
import { ReviewMetricsService, ReviewMetrics } from '../../services/ReviewMetrics/ReviewMetricsService'

const Home = (): JSX.Element => {
  const { user, isAuthenticated } = useAuth0()
  const navigate = useNavigate()
  const [reviewMetrics, setReviewMetrics] = useState<ReviewMetrics | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  // Initialize date range to "this month" by default
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const defaultRange = getPresetDateRange('thisMonth')
    return { ...defaultRange, preset: 'thisMonth' }
  })
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLButtonElement | null>(null)

  useEffect(() => {
    AppLayoutContext.setPageName('Home')
  }, [])

  // Fetch metrics for admin dashboard
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true)
      const service = new ReviewMetricsService()
      const metrics = await service.getReviewMetrics(
        {
          start: dateRange.start || undefined,
          end: dateRange.end || undefined,
        },
        'hospital', // Always pass 'hospital' as review_type
      )
      setReviewMetrics(metrics)
      setLoading(false)
    }
    fetchMetrics()
  }, [dateRange])

  const dashboardCards = [
    {
      title: 'Hospitals',
      description: 'Manage and search hospital information',
      icon: <HospitalIcon />,
      path: '/hospitals',
      color: '#007C91',
      bgGradient: 'linear-gradient(135deg, #007C91 0%, #0097A7 100%)',
    },
    {
      title: 'Agencies',
      description: 'Manage and search agency information',
      icon: <AgencyIcon />,
      path: '/agencies',
      color: '#4DD0E1',
      bgGradient: 'linear-gradient(135deg, #4DD0E1 0%, #26C6DA 100%)',
    },
    {
      title: 'Reported Reviews',
      description: 'Review and manage reported reviews',
      icon: <RateReviewIcon />,
      path: '/admin/reviews',
      color: '#FF7043',
      bgGradient: 'linear-gradient(135deg, #FF7043 0%, #FF5722 100%)',
    },
    {
      title: 'Hospital Claim Pages',
      description: 'View and manage pending claim requests',
      //icon: <AssignmentIcon />,
      path: '/admin/claim-pages',
      color: '#FFB300', // amber
      bgGradient: 'linear-gradient(135deg, #FFB300 0%, #FFA000 100%)',
    },
    {
      title: 'Agency Claim Pages',
      description: 'View and manage pending agency claim requests',
      icon: <AssignmentIcon />,
      path: '/admin/claim-pages?type=agency',
      color: '#4CAF50', // green
      bgGradient: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
    },
    {
      title: 'New Hospital Requests',
      description: 'Review and approve new hospital submissions',
      icon: <HospitalIcon />,
      path: '/admin/hospital-requests',
      color: '#9C27B0', // purple
      bgGradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
    },
    {
      title: 'New Agency Requests',
      description: 'Review and approve new agency submissions',
      icon: <AgencyIcon />,
      path: '/admin/agency-requests',
      color: '#FF6F00', // deep orange
      bgGradient: 'linear-gradient(135deg, #FF6F00 0%, #E65100 100%)',
    },
  ]

  // Skeleton for metric cards
  const MetricCardSkeleton = () => (
    <Card
      sx={{
        height: '100%',
        border: 'none',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        background: '#FFFFFF',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={64} height={64} sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="60%" height={48} />
            <Skeleton variant="text" width="50%" height={28} sx={{ mt: 1 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  )

  // Skeleton for quick access cards
  const QuickAccessCardSkeleton = () => (
    <Card
      sx={{
        height: '100%',
        border: 'none',
        borderRadius: 2,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
        background: '#FFFFFF',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="70%" height={24} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  )

  // ── Helper: readable date-range label ──
  const dateRangeLabel = (() => {
    const presetLabels: Record<string, string> = {
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      thisWeek: 'This Week',
      lastWeek: 'Last Week',
      thisYear: 'This Year',
      lastYear: 'Last Year',
      today: 'Today',
      yesterday: 'Yesterday',
      allTime: 'All Time',
    }
    if (dateRange.preset && presetLabels[dateRange.preset]) return presetLabels[dateRange.preset]
    if (dateRange.preset) return dateRange.preset.charAt(0).toUpperCase() + dateRange.preset.slice(1).replace(/([A-Z])/g, ' $1')
    if (dateRange.start && dateRange.end) return `${new Date(dateRange.start).toLocaleDateString()} – ${new Date(dateRange.end).toLocaleDateString()}`
    return 'Select Range'
  })()

  // ── KPI card data ──
  const kpiCards = [
    {
      label: 'Conversion Rate',
      value: `${(reviewMetrics?.conversion_rate || 0).toFixed(1)}%`,
      icon: <TrendingUpIcon />,
      color: '#007C91',
      gradient: 'linear-gradient(135deg, #007C91 0%, #00ACC1 100%)',
      lightBg: '#E0F7FA',
    },
    {
      label: 'Median Time',
      value: `${reviewMetrics?.median_time_seconds || 0}s`,
      icon: <SpeedIcon />,
      color: '#0097A7',
      gradient: 'linear-gradient(135deg, #0097A7 0%, #26C6DA 100%)',
      lightBg: '#E0F7FA',
    },
    {
      label: 'Optional Fields',
      value: `${(reviewMetrics?.avg_optional_field_completion_rate || 0).toFixed(1)}%`,
      icon: <CheckCircleIcon />,
      color: '#FF7043',
      gradient: 'linear-gradient(135deg, #FF7043 0%, #FF5722 100%)',
      lightBg: '#FBE9E7',
    },
    {
      label: 'Started Reviews',
      value: `${reviewMetrics?.started_count || 0}`,
      icon: <PeopleIcon />,
      color: '#26A69A',
      gradient: 'linear-gradient(135deg, #26A69A 0%, #00897B 100%)',
      lightBg: '#E0F2F1',
    },
    {
      label: 'Submitted Reviews',
      value: `${reviewMetrics?.submitted_count || 0}`,
      icon: <RateReviewIcon />,
      color: '#7B1FA2',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
      lightBg: '#F3E5F5',
    },
  ]

  // ── Quick-access categories ──
  const quickAccessCategories = [
    {
      title: 'Data Management',
      icon: <StorageIcon sx={{ fontSize: 18, color: '#007C91' }} />,
      items: dashboardCards.filter((c) => ['/hospitals', '/agencies'].includes(c.path)),
    },
    {
      title: 'Reviews & Claims',
      icon: <InsightsIcon sx={{ fontSize: 18, color: '#FF7043' }} />,
      items: dashboardCards.filter((c) =>
        ['/admin/reviews', '/admin/claim-pages'].some((p) => c.path.startsWith(p)),
      ),
    },
    {
      title: 'New Requests',
      icon: <NewReleasesIcon sx={{ fontSize: 18, color: '#9C27B0' }} />,
      items: dashboardCards.filter((c) =>
        ['/admin/hospital-requests', '/admin/agency-requests'].includes(c.path),
      ),
    },
  ]

  // Render admin dashboard
  const renderAdminDashboard = () => (
    <Box sx={{ pb: 4 }}>
      {/* ════════════ Hero Banner ════════════ */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 4, sm: 6 },
          borderRadius: 4,
          background: 'linear-gradient(135deg, #00363D 0%, #007C91 40%, #00ACC1 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            right: -60,
            top: -60,
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 80,
            bottom: -100,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          }}
        />
        <Box sx={{ position: 'absolute', left: -30, bottom: -30, opacity: 0.04 }}>
          <DashboardIcon sx={{ fontSize: 220 }} />
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Admin Dashboard
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                {isAuthenticated && user ? `Welcome back, ${user.name || 'Admin'}` : 'Dashboard Overview'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                Monitor review analytics, manage claims, and oversee platform operations
              </Typography>
            </Box>

            {/* Date range picker button */}
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon sx={{ fontSize: '18px !important' }} />}
              onClick={(e) => {
                setDatePickerAnchor(e.currentTarget)
                setDatePickerOpen(true)
              }}
              sx={{
                mt: { xs: 1, sm: 0 },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 2.5,
                px: 2.5,
                py: 1,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                '& *': { cursor: 'pointer' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                boxShadow: 'none',
              }}
            >
              {dateRangeLabel}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Date Range Picker */}
      <DateRangePicker
        value={dateRange}
        onChange={setDateRange}
        anchorEl={datePickerAnchor}
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
      />

      {/* ════════════ KPI Cards ════════════ */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
          <InsightsIcon sx={{ color: '#007C91', fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#263238' }}>
            Key Metrics
          </Typography>
          <Chip
            label={dateRangeLabel}
            size="small"
            sx={{ bgcolor: '#E0F7FA', color: '#007C91', fontWeight: 600, fontSize: '0.75rem', height: 24 }}
          />
        </Stack>

        <Grid container spacing={2.5}>
          {loading
            ? [1, 2, 3, 4, 5].map((i) => (
                <Grid item xs={6} sm={4} md={4} lg={2.4} key={i}>
                  <MetricCardSkeleton />
                </Grid>
              ))
            : kpiCards.map((kpi) => (
                <Grid item xs={6} sm={4} md={4} lg={2.4} key={kpi.label}>
                  <Card
                    sx={{
                      height: '100%',
                      border: 'none',
                      borderRadius: 3,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      background: '#FFFFFF',
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 12px 28px ${kpi.color}20`,
                        '& .kpi-icon-bg': {
                          transform: 'scale(1.1)',
                        },
                      },
                    }}
                  >
                    {/* Top accent bar */}
                    <Box sx={{ height: 4, background: kpi.gradient }} />
                    <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                      <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
                        <Avatar
                          className="kpi-icon-bg"
                          sx={{
                            width: { xs: 40, sm: 52 },
                            height: { xs: 40, sm: 52 },
                            bgcolor: kpi.lightBg,
                            color: kpi.color,
                            transition: 'transform 0.3s ease',
                          }}
                        >
                          {React.cloneElement(kpi.icon, { sx: { fontSize: { xs: 20, sm: 26 } } })}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: '#1A2027',
                              lineHeight: 1.1,
                              mb: 0.25,
                              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' },
                            }}
                          >
                            {kpi.value}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#78909C', fontWeight: 600, fontSize: { xs: '0.68rem', sm: '0.78rem' } }}
                          >
                            {kpi.label}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
        </Grid>
      </Box>

      {/* ════════════ Main Content Grid ════════════ */}
      <Grid container spacing={3}>
        {/* ──── Left Column: Charts & Tables ──── */}
        <Grid item xs={12} lg={8}>
          {/* Daily Review Counts Chart */}
          <Paper
            sx={{
              mb: 3,
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
              background: '#FFFFFF',
              overflow: 'hidden',
            }}
          >
            {/* Chart header */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 2.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ShowChartIcon sx={{ color: '#007C91', fontSize: { xs: 18, sm: 22 } }} />
                <Typography sx={{ fontWeight: 700, color: '#263238', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                  Daily Review Counts
                </Typography>
              </Stack>
              <Chip
                label={dateRangeLabel}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500, fontSize: '0.72rem', height: 24, borderColor: 'rgba(0,124,145,0.2)', color: '#546E7A', display: { xs: 'none', sm: 'inline-flex' } }}
              />
            </Box>

            <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height={280} sx={{ borderRadius: 2 }} />
              ) : reviewMetrics?.daily_review_counts && reviewMetrics.daily_review_counts.length > 0 ? (
                <Box sx={{ height: 360 }}>
                  {React.createElement(
                    ResponsiveContainer as any,
                    { width: '100%', height: 280 },
                    React.createElement(
                      LineChart as any,
                      {
                        data: reviewMetrics.daily_review_counts.map((item) => ({
                          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                          count: item.count,
                        })),
                        margin: { top: 5, right: 20, left: 10, bottom: 5 },
                      },
                      React.createElement(CartesianGrid as any, {
                        strokeDasharray: '3 3',
                        stroke: 'rgba(0,124,145,0.08)',
                        vertical: false,
                      }),
                      React.createElement(XAxis as any, {
                        dataKey: 'date',
                        stroke: '#B0BEC5',
                        tick: { fill: '#78909C', fontSize: 12 },
                        axisLine: { stroke: 'rgba(0,0,0,0.08)' },
                        tickLine: false,
                      }),
                      React.createElement(YAxis as any, {
                        stroke: '#B0BEC5',
                        tick: { fill: '#78909C', fontSize: 12 },
                        axisLine: false,
                        tickLine: false,
                      }),
                      React.createElement(Tooltip as any, {
                        contentStyle: {
                          backgroundColor: '#FFFFFF',
                          border: '1px solid rgba(0,124,145,0.12)',
                          borderRadius: '10px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                          padding: '10px 14px',
                        },
                        labelStyle: { color: '#263238', fontWeight: 700, marginBottom: 4 },
                        itemStyle: { color: '#007C91', fontWeight: 600 },
                      }),
                      React.createElement(Legend as any, { iconType: 'circle', iconSize: 8, wrapperStyle: { paddingTop: 16 } }),
                      React.createElement(Line as any, {
                        type: 'monotone',
                        dataKey: 'count',
                        stroke: '#007C91',
                        strokeWidth: 3,
                        dot: { fill: '#FFFFFF', stroke: '#007C91', strokeWidth: 2, r: 4 },
                        activeDot: { fill: '#007C91', stroke: '#FFFFFF', strokeWidth: 2, r: 6 },
                        name: 'Reviews',
                      }),
                    ),
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 320,
                    py: 6,
                  }}
                >
                  <Avatar sx={{ width: 64, height: 64, bgcolor: '#E0F7FA', color: '#007C91', mb: 2 }}>
                    <ShowChartIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                    No Chart Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    No daily review counts found for the selected date range.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Dropoff by Screen Table */}
          <Paper
            sx={{
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
              background: '#FFFFFF',
              overflow: 'hidden',
            }}
          >
            {/* Table header */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 2.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <TableChartIcon sx={{ color: '#FF7043', fontSize: { xs: 18, sm: 22 } }} />
                <Typography sx={{ fontWeight: 700, color: '#263238', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                  Dropoff by Screen
                </Typography>
              </Stack>
              {!loading && reviewMetrics?.dropoff_by_step && reviewMetrics.dropoff_by_step.length > 0 && (
                <Chip
                  label={`${reviewMetrics.dropoff_by_step.length} steps`}
                  size="small"
                  sx={{ bgcolor: '#FBE9E7', color: '#E64A19', fontWeight: 600, fontSize: '0.72rem', height: 24 }}
                />
              )}
            </Box>

            {loading ? (
              <Box sx={{ p: 3 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" width="100%" height={44} sx={{ borderRadius: 1, mb: 1 }} />
                ))}
              </Box>
            ) : reviewMetrics?.dropoff_by_step && reviewMetrics.dropoff_by_step.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#455A64', py: 1.75, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Screen
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#455A64', py: 1.75, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Reached
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#455A64', py: 1.75, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Abandoned
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#455A64', py: 1.75, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Dropoff
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reviewMetrics.dropoff_by_step
                      .sort((a, b) => (b.dropoff_percentage || 0) - (a.dropoff_percentage || 0))
                      .map((step, index: number) => {
                        const pct = step.dropoff_percentage != null ? step.dropoff_percentage : 0
                        const pctStr = pct.toFixed(1)
                        const severity = pct > 30 ? '#D32F2F' : pct > 15 ? '#ED6C02' : '#2E7D32'
                        return (
                          <TableRow
                            key={index}
                            sx={{
                              transition: 'background 0.2s',
                              '&:hover': { bgcolor: '#F5F7FA' },
                              '&:last-child td': { borderBottom: 0 },
                            }}
                          >
                            <TableCell sx={{ color: '#263238', fontWeight: 500, py: 2 }}>
                              {step.step_name}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#455A64', py: 2 }}>
                              {step.users_reached || 0}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#455A64', py: 2 }}>
                              {Number(step.abandoned_count ?? step.abandonedCount ?? 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Chip
                                label={`${pctStr}%`}
                                size="small"
                                sx={{
                                  bgcolor: `${severity}14`,
                                  color: severity,
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  height: 26,
                                  minWidth: 60,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: '#FBE9E7', color: '#FF7043', mb: 2 }}>
                  <TableChartIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  No Dropoff Data
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  No dropoff data found for the selected date range.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ──── Right Column: Quick Access ──── */}
        <Grid item xs={12} lg={4}>
          <Paper
            sx={{
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
              background: '#FFFFFF',
              overflow: 'hidden',
              position: 'sticky',
              top: 100,
            }}
          >
            {/* Quick access header */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 2.5 },
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <DashboardIcon sx={{ color: '#007C91', fontSize: { xs: 18, sm: 22 } }} />
              <Typography sx={{ fontWeight: 700, color: '#263238', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                Quick Access
              </Typography>
            </Box>

            <Box sx={{ p: 2.5 }}>
              {loading ? (
                <Stack spacing={1.5}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <QuickAccessCardSkeleton key={i} />
                  ))}
                </Stack>
              ) : (
                <Stack spacing={3}>
                  {quickAccessCategories.map((cat) => (
                    <Box key={cat.title}>
                      {/* Category label */}
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5, px: 0.5 }}>
                        {cat.icon}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: '#90A4AE',
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            fontSize: '0.68rem',
                          }}
                        >
                          {cat.title}
                        </Typography>
                      </Stack>

                      <Stack spacing={1}>
                        {cat.items.map((card) => (
                          <Card
                            key={card.path}
                            onClick={() => navigate(card.path)}
                            sx={{
                              cursor: 'pointer',
                              border: '1px solid rgba(0,0,0,0.06)',
                              borderRadius: 2.5,
                              boxShadow: 'none',
                              transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                              '&:hover': {
                                transform: 'translateX(6px)',
                                boxShadow: `0 4px 16px ${card.color}18`,
                                borderColor: `${card.color}40`,
                                '& .qa-arrow': { opacity: 1, transform: 'translateX(0)' },
                              },
                            }}
                          >
                            <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    background: card.bgGradient,
                                    boxShadow: `0 3px 10px ${card.color}30`,
                                  }}
                                >
                                  {card.icon
                                    ? React.cloneElement(card.icon as React.ReactElement, {
                                        sx: { fontSize: 20, color: '#fff' },
                                      })
                                    : null}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A2027', mb: 0.15, fontSize: '0.88rem' }}>
                                    {card.title}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#90A4AE', fontSize: '0.75rem', lineHeight: 1.3 }}>
                                    {card.description}
                                  </Typography>
                                </Box>
                                <ArrowForwardIcon
                                  className="qa-arrow"
                                  sx={{
                                    color: card.color,
                                    fontSize: 18,
                                    opacity: 0.3,
                                    transform: 'translateX(-4px)',
                                    transition: 'all 0.25s ease',
                                  }}
                                />
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )

  return renderAdminDashboard()
}

export default Home
